import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button, theme } from "uikit";

import ReceiptTransaction from "../../../../../services/ReceiptTransaction";
import {
	ReceiptStatus,
	ReceiptSummary,
} from "../../../../../services/ReceiptProvider/types";
import { useOrderReceiptTransaction } from "../../../../../hooks";
import { resolveReceiptSkipReasonMessage } from "../../../../../utils/receiptSkipReason.util";
import { resolveReceiptLastErrorMessage } from "../../../../../utils/receiptLastError.util";
import {
	RECEIPT_DISPLAY_COLORS,
	ReceiptDisplayKind,
	isReceiptStatusInProgress,
	resolveReceiptDisplayKind,
	resolveReceiptDisplayLabel,
	shouldShowReceiptInOrderBottom,
} from "../../../../../utils/receiptDisplay.util";
import ReceiptMessageText from "../../../../ReceiptMessageText";
import Divider from "../../../../Divider";
import { saveBlobWithDialog } from "../../../../../utils/saveBlobWithDialog.util";
import { StyledColumn, StyledRow } from "../../../../common";

const Container = styled(StyledColumn)`
	gap: 6px;
	padding: 8px 0;
	flex-shrink: 0;
`;

const InlineContainer = styled(StyledRow)`
	gap: 10px;
	align-items: center;
	flex-wrap: wrap;
	padding: 8px 0;
	flex-shrink: 0;
`;

const SectionLabel = styled.span`
	font-size: 12px;
	color: ${theme.colors.primary};
	font-weight: 800;
	text-transform: uppercase;
`;

const StatusBadge = styled.span<{
	$kind: ReceiptDisplayKind;
	$processing?: boolean;
}>`
	display: inline-block;
	padding: 4px 12px;
	border-radius: 12px;
	font-size: 12px;
	font-weight: 600;
	line-height: 1.4;
	color: #fff;
	background: ${({ $kind, $processing }) =>
		$processing
			? theme.colors.primary ?? "#3380e6"
			: RECEIPT_DISPLAY_COLORS[$kind]};
`;

const DetailText = styled(ReceiptMessageText)`
	font-size: 12px;
	color: rgba(33, 51, 63, 0.85);
`;

export interface OrderReceiptExtrasContext {
	successTransaction: ReturnType<
		typeof useOrderReceiptTransaction
	>["successTransaction"];
	canPreviewReceipt: boolean;
	reload: () => Promise<void>;
}

interface OrderReceiptProps {
	orderId?: number;
	receipt?: ReceiptSummary | null;
	layout?: "stacked" | "inline";
	onlySuccessOrFailed?: boolean;
	withDivider?: boolean;
	renderExtras?: (context: OrderReceiptExtrasContext) => React.ReactNode;
}

const OrderReceipt: React.FC<OrderReceiptProps> = ({
	orderId,
	receipt,
	layout = "stacked",
	onlySuccessOrFailed = false,
	withDivider = false,
	renderExtras,
}) => {
	const { t } = useTranslation();
	const [retrying, setRetrying] = useState(false);
	const {
		transaction,
		successTransaction,
		canPreviewReceipt,
		loading,
		reload,
		applyTransaction,
		pollUntilSettled,
	} = useOrderReceiptTransaction(orderId, { limit: 5 });

	const effectiveStatus = transaction?.status ?? receipt?.status ?? null;
	const effectiveSkipReason =
		transaction?.skipReason ?? receipt?.skipReason ?? null;
	const preferredTransactionId =
		transaction?.id ?? receipt?.transactionId ?? null;

	const handleRetry = useCallback(async () => {
		if (!orderId) return;

		setRetrying(true);
		try {
			const transactionId =
				await ReceiptTransaction.resolveRetryTransactionId(
					orderId,
					preferredTransactionId,
				);

			if (!transactionId) {
				throw new Error(
					t(
						"orderPageWidgets.orderDetails.receipt.retryUnavailable",
					) ?? "Не удалось найти транзакцию для повтора",
				);
			}

			const updated = await ReceiptTransaction.retry(transactionId);
			if (updated) {
				applyTransaction(updated);
				if (isReceiptStatusInProgress(updated.status)) {
					await pollUntilSettled();
				}
			}

			await reload();
		} finally {
			setRetrying(false);
		}
	}, [
		orderId,
		preferredTransactionId,
		applyTransaction,
		pollUntilSettled,
		reload,
		t,
	]);

	const handleDownload = useCallback(async () => {
		const downloadId = successTransaction?.id ?? preferredTransactionId;
		if (!downloadId) return;
		const result = await ReceiptTransaction.downloadImage(downloadId);
		if (!result) return;

		await saveBlobWithDialog({
			blob: result.blob,
			fileName: result.fileName,
			mimeType: result.contentType,
		});
	}, [successTransaction?.id, preferredTransactionId]);

	const tBase = "orderPageWidgets.orderDetails.receipt";

	const displayKind = useMemo(
		() => resolveReceiptDisplayKind(effectiveStatus),
		[effectiveStatus],
	);

	const statusLabel = useMemo(
		() =>
			effectiveStatus
				? resolveReceiptDisplayLabel(t, {
						status: effectiveStatus,
						skipReason: effectiveSkipReason,
				  })
				: t(`${tBase}.notApplicable`) ?? "Без чека",
		[t, effectiveStatus, effectiveSkipReason, tBase],
	);

	const skipReasonMessage = useMemo(() => {
		if (
			displayKind !== ReceiptDisplayKind.Skipped &&
			displayKind !== ReceiptDisplayKind.NotApplicable
		) {
			return null;
		}
		return resolveReceiptSkipReasonMessage(t, effectiveSkipReason);
	}, [displayKind, t, effectiveSkipReason]);

	const lastErrorMessage = useMemo(() => {
		if (displayKind !== ReceiptDisplayKind.Failed) return null;
		return resolveReceiptLastErrorMessage(t, transaction?.lastError);
	}, [displayKind, t, transaction?.lastError]);

	const hasReceiptRecord = Boolean(transaction || receipt?.status);
	const canDownload =
		canPreviewReceipt ||
		(receipt?.status === ReceiptStatus.SUCCESS &&
			!!receipt?.transactionId &&
			!!receipt?.externalId);
	const isFailed = displayKind === ReceiptDisplayKind.Failed;
	const isProcessing = retrying || isReceiptStatusInProgress(effectiveStatus);
	const usesSuccessPreview =
		Boolean(renderExtras) &&
		displayKind === ReceiptDisplayKind.Success &&
		canPreviewReceipt;

	const detailMessage = useMemo(() => {
		if (isFailed) {
			return (
				lastErrorMessage ??
				(loading
					? t(`${tBase}.loading`) ?? "Loading…"
					: t(`${tBase}.failedSummary`) ??
					  "Попытка отправить чек была, но ECC не принял заказ")
			);
		}

		if (skipReasonMessage) {
			return skipReasonMessage;
		}

		if (
			displayKind === ReceiptDisplayKind.NotApplicable &&
			(!effectiveStatus || !hasReceiptRecord)
		) {
			return (
				t(`${tBase}.notApplicableHint`) ??
				"Для этого заказа отправка чека не выполнялась"
			);
		}

		return null;
	}, [
		displayKind,
		effectiveStatus,
		hasReceiptRecord,
		isFailed,
		lastErrorMessage,
		loading,
		skipReasonMessage,
		t,
		tBase,
	]);

	const titleLabel = t(`${tBase}.title`) ?? "Electronic receipt";
	const badgeLabel = isProcessing
		? t(`${tBase}.processing`) ?? t(`${tBase}.loading`) ?? "Отправка…"
		: statusLabel;

	const isVisible = useMemo(() => {
		if (!onlySuccessOrFailed) {
			return true;
		}

		const statusHint = loading
			? receipt?.status ?? effectiveStatus
			: effectiveStatus;

		return shouldShowReceiptInOrderBottom(statusHint);
	}, [effectiveStatus, loading, onlySuccessOrFailed, receipt?.status]);

	if (!orderId || !isVisible) return null;

	const divider = withDivider ? (
		<Divider
			orientation="horizontal"
			size="max"
			color="rgb(33, 51, 63, 0.15)"
		/>
	) : null;

	if (loading && !hasReceiptRecord) {
		if (layout === "inline") {
			return (
				<>
					{divider}
					<InlineContainer>
						<SectionLabel>{titleLabel}</SectionLabel>
						<span>{t(`${tBase}.loading`) ?? "Loading…"}</span>
					</InlineContainer>
				</>
			);
		}

		return (
			<Container>
				<SectionLabel>{titleLabel}</SectionLabel>
				<span>{t(`${tBase}.loading`) ?? "Loading…"}</span>
			</Container>
		);
	}

	if (layout === "inline") {
		return (
			<>
				{divider}
				<InlineContainer>
					<SectionLabel>{titleLabel}</SectionLabel>
					<StatusBadge $kind={displayKind} $processing={isProcessing}>
						{badgeLabel}
					</StatusBadge>
					{detailMessage ? (
						<DetailText title={transaction?.lastError ?? undefined}>
							{detailMessage}
						</DetailText>
					) : null}
					{canDownload && !usesSuccessPreview ? (
						<Button.Button
							variant="primary"
							onClick={handleDownload}
							text={
								t(`${tBase}.actions.download`) ??
								"Download receipt"
							}
						/>
					) : null}
					{isFailed ? (
						<Button.Button
							variant="secondary"
							disabled={isProcessing || loading}
							onClick={handleRetry}
							text={
								isProcessing
									? t(`${tBase}.processing`) ??
									  t(`${tBase}.loading`) ??
									  "Отправка…"
									: t(`${tBase}.actions.retry`) ??
									  "Повторить отправку"
							}
						/>
					) : null}
					{renderExtras?.({
						successTransaction,
						canPreviewReceipt,
						reload,
					})}
				</InlineContainer>
			</>
		);
	}

	return (
		<Container>
			<StyledRow gap="10px" alignItems="center" flex={{ wrap: "wrap" }}>
				<SectionLabel>{titleLabel}</SectionLabel>
				<StatusBadge $kind={displayKind} $processing={isProcessing}>
					{badgeLabel}
				</StatusBadge>
			</StyledRow>

			{detailMessage ? (
				<DetailText title={transaction?.lastError ?? undefined}>
					{detailMessage}
				</DetailText>
			) : null}

			{canDownload && !usesSuccessPreview && (
				<StyledRow gap="6px" flex={{ wrap: "wrap" }}>
					<Button.Button
						variant="primary"
						onClick={handleDownload}
						text={
							t(`${tBase}.actions.download`) ?? "Download receipt"
						}
					/>
				</StyledRow>
			)}

			{isFailed && (
				<StyledRow gap="6px" flex={{ wrap: "wrap" }}>
					<Button.Button
						variant="secondary"
						disabled={isProcessing || loading}
						onClick={handleRetry}
						text={
							isProcessing
								? t(`${tBase}.processing`) ??
								  t(`${tBase}.loading`) ??
								  "Отправка…"
								: t(`${tBase}.actions.retry`) ??
								  "Повторить отправку"
						}
					/>
				</StyledRow>
			)}

			{renderExtras?.({
				successTransaction,
				canPreviewReceipt,
				reload,
			})}
		</Container>
	);
};

export default OrderReceipt;
